import { Component, OnInit, Input, Output, EventEmitter, Self, Optional, TemplateRef, forwardRef, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.less'],
})
export class SearchComponent implements OnInit, ControlValueAccessor, OnChanges {
  @Input() placeholder: string;
  @Input() debounceTime: number = 300;
  @Input() size: string;
  @Input() customClass: string;
  @Input() isDisabled = false;
  @Input() maxLength = 0;
  @Input()
  set value(val: any) {
    this.searchValue = val;
    if (val) {
      if (typeof val === 'string') this.searchValue = val;
      else this.searchValue = val[this.labelKey];
    }
  }
  @Input() minChar: number = 3;
  @Input() loader: boolean = true;
  @Input() label: string;
  @Input() isRequired: boolean;
  @Input() isError: boolean = true;
  @Input() errorMessage: string;
  @Input() template: TemplateRef<any>;
  @Input()
  set stopLoader(val: boolean) {
    if (typeof val === 'boolean') this.showLoader = !val;
  }
  @Input() labelKey: string = 'label';
  @Input() valueKey: string = 'value';
  @Input() description: string = '';
  @Input() highlight: boolean = false;
  @Input() list: any[] = [];
  @Input() listMaxHeight: number = 150;
  @Input() overflowLimit: number = 10;
  @Input() onlySearch: boolean = false;

  _list: ListItem[] = [];
  searchValue: string = '';
  searchValue$: Subject<string> = new Subject();
  showLoader: boolean = false;
  opened: boolean = false;
  noResultsFound: boolean = false;
  focus: boolean = false;

  @Output() changeValue: EventEmitter<string> = new EventEmitter();
  @Output() fieldCleared: EventEmitter<void> = new EventEmitter();
  @Output() selected: EventEmitter<any> = new EventEmitter();

  touch: any = () => {};
  change: any = () => {};

  constructor(@Self() @Optional() private ngControl: NgControl) {
    ngControl && (ngControl.valueAccessor = this);
  }
  writeValue(val: any): void {
    this.searchValue = val;
    if (val) {
      if (typeof val === 'string') this.searchValue = val;
      else this.searchValue = val[this.labelKey];
    }
  }
  registerOnChange(fn: any): void {
    this.change = fn;
  }
  registerOnTouched(fn: any): void {
    this.touch = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngOnInit(): void {
    this.handleValueChange();
  }
  ngOnChanges(changes: SimpleChanges) {
    const { list } = changes;
    if (list && list.currentValue) {
      this._list = this.list
        .filter((i, index) => index < this.overflowLimit)
        .map((val) => {
          const item: ListItem = {
            heading: val[this.labelKey],
            value: val[this.valueKey],
          };
          return item;
        });
      this.noResultsFound = this._list.length ? false : true;
    }
  }
  private handleValueChange() {
    this.searchValue$
      .pipe(
        tap((_) => {
          if (this.ngControl) this.ngControl.control.markAsDirty();

          this._list = [];
          this.noResultsFound = false;
        }),
        debounceTime(this.debounceTime)
      )
      .subscribe((val: string) => {
        if (val.length >= this.minChar) {
          if (this.loader) this.showLoader = true;
          this.opened = true;
          if (this.onlySearch) {
            this.change(val);
          }
          this.changeValue.emit(val);
        }
        if (!val.length) {
          if (this.onlySearch) this.change(val);
          this.clear();
        }
      });
  }
  clear() {
    this.change('');
    this.touch();
    if (this.ngControl) this.ngControl.control.markAsDirty();
    this.opened = false;
    this.fieldCleared.emit();
    this.searchValue = '';
    this.focus = false;
  }
  focusin() {
    this.touch();
    this.focus = true;
  }
  click(event: MouseEvent) {
    event.stopPropagation();
  }
  get showError() {
    if (!this.focus) {
      if (this.ngControl) {
        return this.ngControl.invalid && this.ngControl.touched;
      }
      return this.isError;
    }
  }
  select(item: ListItem) {
    this.searchValue = item.heading;
    const selected = this.list.find((i) => i[this.valueKey] === item.value);
    this.change(selected);
    this.selected.emit(selected);
    this.opened = false;
  }
}
interface ListItem {
  heading: string;
  value: string;
}
